import { Link } from 'expo-router';
import { useState } from 'react';
import {
  ActivityIndicator,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { apiService } from '../../services/apiService';
import { COLORS } from '../../styles/colors';
import { fontFamilies } from '../../utils/fontLoader';
import logger from '../../utils/logger';
import TurnstileWidget from '../common/TurnstileWidget';

const ContactUsForm = ({ visible, onClose }) => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [agree, setAgree] = useState(false);
  const [isEmailValid, setIsEmailValid] = useState(true);

  const [turnstileToken, setTurnstileToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formMessage, setFormMessage] = useState('');
  const [isSuccess, setIsSuccess] = useState(false);

  if (!visible) {
    return null;
  }

  const validateEmail = (text) => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const isValid = emailRegex.test(text);
    setIsEmailValid(isValid);
    setEmail(text);
  };

  const resetForm = () => {
    setName('');
    setEmail('');
    setMessage('');
    setAgree(false);
    setTurnstileToken(null);
    // Explicitly reset the Turnstile widget using its API
    if (window.turnstile) {
      logger.log('Resetting Turnstile widget...');
      window.turnstile.reset();
    }
  };

  const handleSubmit = async () => {
    if (!turnstileToken) {
      setFormMessage('Please complete the security check.');
      setIsSuccess(false);
      return;
    }

    setIsSubmitting(true);
    setFormMessage('');

    try {
      const { error } = await apiService.invokeEdgeFunction('handle-contact-form', {
        body: { name, email, message, turnstileToken },
      });

      if (error) throw new Error(error.message);

      setFormMessage('Message sent successfully! We will get back to you shortly.');
      setIsSuccess(true);
      resetForm();

      setTimeout(() => {
        onClose();
        setFormMessage(''); // Also clear the message when closing
      }, 3000);
    } catch (error) {
      setFormMessage(`Error: ${error.message}`);
      setIsSuccess(false);
    } finally {
      setIsSubmitting(false);
    }
  };

  const isSubmitDisabled = !agree || !isEmailValid || !email || !turnstileToken || isSubmitting;

  return (
    <View style={styles.formContainer}>
      <TouchableOpacity onPress={onClose} style={styles.closeButton}>
        <Text style={styles.closeButtonText}>X</Text>
      </TouchableOpacity>
      <Text style={styles.title}>Contact Us</Text>

      {/* ... (Your TextInputs and Switch remain the same) ... */}
      <TextInput style={styles.input} placeholder="Name" placeholderTextColor="rgba(255, 255, 255, 0.35)" value={name} onChangeText={setName} />
      <TextInput
        style={[styles.input, !isEmailValid && styles.inputError]}
        placeholder="Email Address"
        placeholderTextColor="rgba(255, 255, 255, 0.35)"
        value={email}
        onChangeText={validateEmail}
        keyboardType="email-address"
        autoCapitalize="none"
      />
      {!isEmailValid && <Text style={styles.errorText}>Please enter a valid email address.</Text>}
      <TextInput
        style={[styles.input, styles.messageInput]}
        placeholder="Message"
        placeholderTextColor="rgba(255, 255, 255, 0.35)"
        value={message}
        onChangeText={setMessage}
        multiline
      />
      <View style={styles.consentContainer}>
        <Switch
          value={agree}
          onValueChange={setAgree}
          trackColor={{ false: '#767577', true: COLORS.primary }}
          thumbColor={agree ? COLORS.primary : '#f4f3f4'}
        />
        <Text style={styles.consentText}>
          I agree to have my information processed in order to respond to my inquiry. Please review
          our{' '}
          <Link href="/privacy-policy" style={styles.linkText}>
            Privacy Policy
          </Link>
          .
        </Text>
      </View>

      <View style={styles.turnstileContainer}>
        <TurnstileWidget onTokenReceived={setTurnstileToken} invisible />
      </View>

      <TouchableOpacity
        style={[styles.submitButton, isSubmitDisabled && styles.submitButtonDisabled]}
        onPress={handleSubmit}
        disabled={isSubmitDisabled}
      >
        {isSubmitting ? (
          <ActivityIndicator color="#fff" />
        ) : (
          <Text style={styles.submitButtonText}>Submit</Text>
        )}
      </TouchableOpacity>

      {formMessage ? (
        <Text style={[styles.formMessage, isSuccess ? styles.successMessage : styles.errorMessage]}>
          {formMessage}
        </Text>
      ) : null}
    </View>
  );
};

// ... (Your existing styles remain unchanged)
const styles = StyleSheet.create({
  formContainer: {
    position: 'absolute',
    bottom: 20,
    right: 20,
    width: 350,
    backgroundColor: '#1A1F2E',
    borderRadius: 12,
    padding: 20,
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.1)',
    boxShadow: '0px 4px 24px rgba(0, 0, 0, 0.5)',
    zIndex: 1000,
  },
  closeButton: { position: 'absolute', top: 10, right: 10 },
  closeButtonText: { fontSize: 18, fontFamily: fontFamilies.satoshi.bold, color: 'rgba(255, 255, 255, 0.6)' },
  title: { fontSize: 20, fontFamily: fontFamilies.satoshi.bold, marginBottom: 20, color: '#FFFFFF' },
  input: {
    borderWidth: 1,
    borderColor: 'rgba(255, 255, 255, 0.15)',
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    borderRadius: 8,
    padding: 10,
    marginBottom: 15,
    fontFamily: fontFamilies.satoshi.regular,
    color: '#FFFFFF',
  },
  inputError: { borderColor: '#EF4444' },
  errorText: {
    color: '#EF4444',
    fontSize: 12,
    marginBottom: 10,
    fontFamily: fontFamilies.satoshi.regular,
  },
  messageInput: { height: 100, textAlignVertical: 'top' },
  consentContainer: { flexDirection: 'row', alignItems: 'center', marginBottom: 20 },
  consentText: {
    marginLeft: 10,
    fontFamily: fontFamilies.satoshi.regular,
    fontSize: 12,
    flex: 1,
    lineHeight: 18,
    color: 'rgba(255, 255, 255, 0.7)',
  },
  linkText: {
    color: '#4A8BFF',
    textDecorationLine: 'underline',
  },
  submitButton: {
    backgroundColor: COLORS.primary,
    padding: 15,
    borderRadius: 8,
    alignItems: 'center',
  },
  submitButtonDisabled: { backgroundColor: 'rgba(255, 255, 255, 0.1)' },
  submitButtonText: { color: 'white', fontFamily: fontFamilies.satoshi.bold },
  turnstileContainer: { alignItems: 'center', marginBottom: 20 },
  formMessage: { marginTop: 15, textAlign: 'center', fontSize: 14, padding: 10, borderRadius: 8 },
  successMessage: { color: '#4ADE80', backgroundColor: 'rgba(74, 222, 128, 0.1)' },
  errorMessage: { color: '#EF4444', backgroundColor: 'rgba(239, 68, 68, 0.1)' },
});

export default ContactUsForm;
